import React from 'react';
import { Box, Typography } from '@mui/material';

import GameTagCard from './tagCard';

/**
 * The button board, in three sections.
 *
 * Each button is an object rather than a bare string because two things vary
 * independently now:
 *
 *   label  what the coach sees, and the button's identity - it is what marks a
 *          button selected, and what picks its icon and colour.
 *   tag    which definition to query. Several buttons share one: Attacking
 *          Corners and Defending Corners are the same definition read from
 *          opposite sides, as are Gk Distribution and Opponent Gk Press.
 *   side   'our' or 'opponent'. Every button states its own, which is why there
 *          is no Our Team / Opponent toggle: the board shows both perspectives
 *          at once, green for attacking and red for defending.
 *
 * Buttons whose `tag` is not in TAG_KEYS still fall through to their original
 * per-button endpoints, so a definition that does not exist yet renders and is
 * simply inert rather than breaking the board.
 */

const TEAM_ANALYSIS = [
    // Laid out as seven columns, two rows: every attacking button sits directly
    // above the defending button that mirrors it, so a column reads as one idea
    // seen from both sides. Row one is green, row two red - that pairing is the
    // whole reason the Our Team / Opponent toggle could go away.
    { label: 'All Attacking Possessions', tag: 'All Offensive Possessions', side: 'our' },
    { label: 'Gk Distribution', tag: 'Started From Goalkeeper', side: 'our' },
    { label: 'Build Up Under Pressure', tag: 'Build Up Own Half', side: 'our' },
    { label: 'Attack', tag: 'Build Up Opponent Half', side: 'our' },
    { label: 'All Transitions from Defense to Attack', tag: 'All Transitions from Defense to Attack', side: 'our' },
    { label: 'Counter Attack after pass interception', tag: 'Counter Attack after pass interception', side: 'our' },
    { label: 'Counter Attack after 1v1', tag: 'Counter Attack after 1v1', side: 'our' },

    { label: 'All Defensive Possessions', tag: 'All Defensive Possessions', side: 'our' },
    { label: 'Opponent Gk Press', tag: 'Started From Goalkeeper', side: 'opponent' },
    { label: 'High Press', tag: 'Build Up Own Half', side: 'opponent' },
    { label: 'Defense', tag: 'Build Up Opponent Half', side: 'opponent' },
    { label: 'All Transitions from Attack to Defense', tag: 'All Transitions from Defense to Attack', side: 'opponent' },
    { label: 'Counter Defense after pass interception', tag: 'Counter Attack after pass interception', side: 'opponent' },
    { label: 'Counter Defense after 1v1', tag: 'Counter Attack after 1v1', side: 'opponent' }
];

const SET_PIECES = [
    { label: 'Kick-Offs', tag: 'Kick-Offs', side: 'our' },
    { label: 'Attacking Corners', tag: 'Started From Corners', side: 'our' },
    { label: 'Attacking Free Kicks', tag: 'Started From Free Kicks', side: 'our' },
    { label: 'Attacking Throw Ins', tag: 'Started From Throw In', side: 'our' },
    { label: 'Defending Corners', tag: 'Started From Corners', side: 'opponent' },
    { label: 'Defending Free Kicks', tag: 'Started From Free Kicks', side: 'opponent' },
    { label: 'Defending Throw Ins', tag: 'Started From Throw In', side: 'opponent' }
];

const INDIVIDUAL_ACTIONS = [
    // Same column pairing as Team Analysis: our action on top, the
    // opponent's directly below it.
    { label: 'Goals', tag: 'Goals', side: 'our' },
    { label: 'Chances', tag: 'Chances', side: 'our' },
    { label: 'Offsides', tag: 'Offsides', side: 'our' },
    { label: 'Saved', tag: 'Saved', side: 'our' },
    { label: 'Blocked', tag: 'Blocked', side: 'our' },
    { label: 'Clearance', tag: 'Clearance', side: 'our' },
    { label: 'Started from successful 1v1 and become chance', tag: 'Started from successful 1v1 and become chance', side: 'our' },

    { label: 'Opponent Goals', tag: 'Goals', side: 'opponent' },
    { label: 'Opponent Chances', tag: 'Chances', side: 'opponent' },
    { label: 'Opponent Offsides', tag: 'Offsides', side: 'opponent' },
    { label: 'Opponent Saved', tag: 'Saved', side: 'opponent' },
    { label: 'Opponent Blocked', tag: 'Blocked', side: 'opponent' },
    { label: 'Opponent Clearance', tag: 'Clearance', side: 'opponent' },
    {
        label: 'Started from stolen or unsuccessful 1v1 and become chance for the opponent',
        tag: 'Started from successful 1v1 and become chance',
        side: 'opponent'
    }
];

// The Game page keeps these two; the Team page has no single game to show them for.
const GAME_ONLY = [
    { label: 'Game Highlight', tag: 'Game Highlight', side: 'our' },
    { label: 'Clean Game', tag: 'Clean Game', side: 'our' }
];

const sectionHeading = {
    fontSize: '13px',
    fontWeight: 800,
    color: '#111827',
    textTransform: 'uppercase',
    letterSpacing: '0.9px',
    marginBottom: '7px',
    paddingLeft: '2px'
};

const Section = ({ title, buttons, columns, selectedTag, onShow, loadingTag, t }) => (
    <Box>
        <Typography sx={sectionHeading}>{t(title)}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: '8px' }}>
            {buttons.map((button) => (
                <Box key={button.label} sx={{ display: 'flex' }}>
                <GameTagCard
                    t={t}
                    isSelected={selectedTag === button.label}
                    tagname={button.label}
                    button={button}
                    onShow={onShow}
                    isLoading={loadingTag === button.label}
                />
                </Box>
            ))}
        </Box>
    </Box>
);

const GameTagButtonList = ({ selectedTag, onShow, isTeams, t, loadingTag }) => {
    // Appended, not prepended: prepending would shift every attacking button out of
    // the column its defending mirror sits in.
    const teamAnalysis = isTeams ? TEAM_ANALYSIS : [...TEAM_ANALYSIS, ...GAME_ONLY];

    return (
        <Box
            sx={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
                <Section
                    title="Team Analysis"
                    buttons={teamAnalysis}
                    columns={7}
                    selectedTag={selectedTag}
                    onShow={onShow}
                    loadingTag={loadingTag}
                    t={t}
                />
                <Section
                    title="Set Pieces"
                    buttons={SET_PIECES}
                    columns={7}
                    selectedTag={selectedTag}
                    onShow={onShow}
                    loadingTag={loadingTag}
                    t={t}
                />
                <Section
                    title="Individual Actions"
                    buttons={INDIVIDUAL_ACTIONS}
                    columns={7}
                    selectedTag={selectedTag}
                    onShow={onShow}
                    loadingTag={loadingTag}
                    t={t}
                />
            </Box>
        </Box>
    );
};

export { TEAM_ANALYSIS, SET_PIECES, INDIVIDUAL_ACTIONS, GAME_ONLY };
export default GameTagButtonList;
